import { currentUserQuery } from '@/api/user/userQueries';
import { Spinner } from '@/components/common/Spinner';
import { DashboardFooter } from '@/components/dashboard/DashboardFooter';
import { Header } from '@/components/layout/Header';
import { Role } from '@/domain/Role';
import { rootRoute } from '@/routes/rootRoute';
import { routes } from '@/routes/routes';
import {
  checkoutFailSearchSchema,
  checkoutSuccessSearchSchema,
} from '@/validation/checkoutValidator';
import { useQuery } from '@tanstack/react-query';
import {
  Outlet,
  createRoute,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { AccountPage } from './AccountPage';
import { DashboardPage } from './DashboardPage';
import { MyCartCheckoutFailPage } from './my-carts/MyCartCheckoutFailPage';
import { MyCartCheckoutPage } from './my-carts/MyCartCheckoutPage';
import { MyCartCheckoutSuccessPage } from './my-carts/MyCartCheckoutSuccessPage';
import { MyCartItemsPage } from './my-carts/MyCartItemsPage';
import { MyCartsCreatePage } from './my-carts/MyCartsCreatePage';
import { MyCartsPage } from './my-carts/MyCartsPage';
import { MyOrdersPage } from './MyOrdersPage';
import { OrdersPage } from './OrdersPage';
import { PractitionersPage } from './PractitionersPage';
import { QrCodesPage } from './QrCodesPage';
import { StatisticsPage } from './StatisticsPage';
import { SubscriptionPage } from './SubscriptionsPage';
import { SupplementsPage } from './SupplementsPage';
import { UsersPage } from './UsersPage';

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: user, isLoading } = useQuery(currentUserQuery);

  useEffect(() => {
    if (!isLoading && !user?.data) {
      navigate({ to: routes.auth.login });
      return;
    }

    if (
      user?.data.role === Role.GUEST &&
      location.pathname !== routes.dashboard.index
    ) {
      navigate({ to: routes.dashboard.index });
    }
  }, [isLoading, user, navigate, location]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex flex-1 flex-col justify-center">
        <Outlet />
      </main>

      <DashboardFooter />
    </div>
  );
};

export const dashboardLayoutRoute = createRoute({
  id: '_dashboard-layout',
  getParentRoute: () => rootRoute,
  component: DashboardLayout,
});

export const dashboardRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.index,
  component: DashboardPage,
});

export const accountRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.account,
  component: AccountPage,
});

export const myCartsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.myCarts.index,
  component: MyCartsPage,
});

export const myCartCreateRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.myCarts.create,
  component: MyCartsCreatePage,
});

export const myCartAddItemsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.myCarts.cart.items,
  params: {
    parse: (params) => ({
      cartId: Number(params.cartId),
    }),
  },
  component: MyCartItemsPage,
});

export const myCartSummaryRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.myCarts.cart.checkout,
  params: {
    parse: (params) => ({
      cartId: Number(params.cartId),
    }),
  },
  component: MyCartCheckoutPage,
});

export const myCartCheckoutSuccessRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.myCarts.cart.checkoutSuccess,
  params: {
    parse: (params) => ({
      cartId: Number(params.cartId),
    }),
  },
  validateSearch: (search) => checkoutSuccessSearchSchema.parse(search),
  component: MyCartCheckoutSuccessPage,
});

export const myCartCheckoutFailRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.myCarts.cart.checkoutFail,
  params: {
    parse: (params) => ({
      cartId: Number(params.cartId),
    }),
  },
  validateSearch: (search) => checkoutFailSearchSchema.parse(search),
  component: MyCartCheckoutFailPage,
});

export const ordersRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.orders,
  component: OrdersPage,
});

export const practitionersRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.practitioners,
  component: PractitionersPage,
});

export const ordersHistoryRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.myOrders.index,
  component: MyOrdersPage,
});

export const usersRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.users,
  component: UsersPage,
});

export const subscriptionOrderRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.subscriptionOrder,
  params: {
    parse: (params) => ({
      orderId: Number(params.orderId),
    }),
  },
  component: SubscriptionPage,
});

export const subscriptionCartItemsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.subscription.cart.items,
  params: {
    parse: (params) => ({
      cartId: Number(params.cartId),
    }),
  },
  // `walkin` marks the manual PAID-order flow: the items step then creates a
  // temp order instead of finalizing the cart for the customer.
  validateSearch: (search: Record<string, unknown>): { walkin?: boolean } => ({
    walkin: search.walkin === true || search.walkin === 'true',
  }),
  component: SubscriptionPage,
});

export const supplementsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.supplements,
  component: SupplementsPage,
});

export const statisticsRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.statistics,
  component: StatisticsPage,
});

export const qrCodesRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: routes.dashboard.qrCodes,
  component: QrCodesPage,
});

export const dashboardRoutes = dashboardLayoutRoute.addChildren([
  dashboardRoute,
  accountRoute,
  myCartsRoute,
  myCartCreateRoute,
  myCartAddItemsRoute,
  myCartSummaryRoute,
  myCartCheckoutSuccessRoute,
  myCartCheckoutFailRoute,
  ordersRoute,
  practitionersRoute,
  ordersHistoryRoute,
  usersRoute,
  subscriptionOrderRoute,
  subscriptionCartItemsRoute,
  supplementsRoute,
  statisticsRoute,
  qrCodesRoute,
]);
